import {Inject, Injectable} from '@angular/core';
import {
  InAppNotificationEnum,
  InAppNotificationLogType,
} from '@generated/graphql';
import {
  BehaviorSubject, EMPTY,
  catchError,
  filter,
  finalize,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  inAppNotificationLogModelFactory
} from "@modules/in-app-notification-log/factories/in-app-notification-log.factory";
import {InAppNotificationLog} from "@modules/in-app-notification-log/models/in-app-notification-log.model";
import {EntityService} from "@shared/services/entity.service";
import {EntityFeatureNameEnum} from "@shared/contracts/entity-store.contract";
import {Store} from "@ngrx/store";
import {
  IN_APP_NOTIFICATION_LOG_STORE,
  InAppNotificationLogStore
} from "@modules/in-app-notification-log/store/in-app-notification-log.store";
import {PaginationData} from "@shared/types/pagination.type";
import {BrowserNotificationService} from "@shared/services/browser-notification.service";
import {buildExtraJobsTabQueryParams, JobDetailTab} from "@modules/job/pages/job-detail/job-detail.component";
import {Router} from "@angular/router";
import {APP_ROUTES, AppRoutes} from "@config/app-routes.config";
import InAppNotificationLogApi from "@modules/in-app-notification-log/apis/in-app-notification-log.api";
import {NGXLogger} from "ngx-logger";
import {
  COMPANY_PROFILE_TAB_QUERY_PARAM_VALUES,
  CompanyProfileTab
} from '@modules/company/pages/company-profile-page/company-profile-page.component';
import {ExtraJobApi} from "@modules/extra-job/api/extra-job.api";

type NotificationRedirectTarget = {
  url: any[];
  queryParams?: Record<string, any>;
};

type NotificationDetails = {
  jobId?: string | null;
  extraJobId?: string | null;
};

@Injectable({
  providedIn: 'root',
})
export default class InAppNotificationLogService extends EntityService<EntityFeatureNameEnum.InAppNotificationLog, InAppNotificationLogType, InAppNotificationLog> {
  readonly _notificationsData$ = new BehaviorSubject<{
    paginator: PaginationData<InAppNotificationLog>,
    unreadCount: number
  }>(null)

  readonly notificationsData$ = this._notificationsData$.asObservable()

  readonly notifications$ = this.notificationsData$.pipe(
    map(data => {
      const items = data?.paginator.data ?? []

      return [
        ...items.filter(v => !v.isRead),
        ...items.filter(v => v.isRead)
      ]
    })
  )

  readonly unread$ = this.notificationsData$.pipe(
    filter(data => !!data),
    map(data => {
      const max = 10

      return data.unreadCount <= max ? data.unreadCount : (max + '+')
    }),
  )

  readonly isDisabledMarkingReadAll$ = this.notifications$.pipe(
    map(notifications => !notifications.some(n => !n.isRead))
  );

  public constructor(
    protected readonly store: Store,
    @Inject(IN_APP_NOTIFICATION_LOG_STORE) protected readonly inAppNotificationLogStore: InAppNotificationLogStore,
    private readonly api: InAppNotificationLogApi,
    private readonly browserNotificationService: BrowserNotificationService,
    private readonly router: Router,
    @Inject(APP_ROUTES) public readonly appRoutes: AppRoutes,
    private readonly logger: NGXLogger,
    private readonly extraJobApi: ExtraJobApi,
  ) {
    super(store, inAppNotificationLogStore)
  }

  get factory() {
    return inAppNotificationLogModelFactory;
  }

  clearAll(): void {
    this._notificationsData$.next(null)
    this.logger.info(`[Notification] Clear all!`)
  }

  readonly show$ = (notification: InAppNotificationLog) => this.get$().pipe(
    tap(() => {
      this.browserNotificationService.showNotification('New notification', {
        body: notification.description
      })
    }),
    finalize(() => this.logger.info(`[Notification] Show ${notification.id}!`))
  )

  readonly get$ = () => this.api.get$().pipe(
    tap(data => {
      this._notificationsData$.next(data)
    }),
    finalize(() => this.logger.info(`[Notification] Get data!`))
  )

  readonly markReadAll$ = () => this.api.markReadAll$().pipe(
    switchMap(() => this.get$()),
    finalize(() => this.logger.info(`[Notification] Read all!`))
  )

  readonly readThenRedirect$ = (log: InAppNotificationLog) => this.api.read$(log.id).pipe(
    switchMap(() => this.get$()),
    switchMap(() => this.redirect$(log))
  )

  private redirect$(log: InAppNotificationLog) {
    return this.getRedirectTarget$(log).pipe(
      switchMap((target) => {
        if (!target) {
          return EMPTY;
        }

        return from(this.router.navigate(target.url, {queryParams: target.queryParams}));
      }),
      finalize(() => this.logger.info(`[Notification] Redirected to ${log.id}!`))
    );
  }

  private getRedirectTarget$(log: InAppNotificationLog): Observable<NotificationRedirectTarget | null> {
    const details = this.parseDetails(log.details);

    switch (log.type) {
      case InAppNotificationEnum.CompanyProfileCreated:
        return of(log.entityId ? {url: this.appRoutes.company(log.entityId)} : null);

      case InAppNotificationEnum.CompleteKyc:
        return of(log.entityId ? {
          url: this.appRoutes.company(log.entityId),
          queryParams: {tab: COMPANY_PROFILE_TAB_QUERY_PARAM_VALUES[CompanyProfileTab.Kyc]},
        } : null);

      case InAppNotificationEnum.ExtraJobCreated:
      case InAppNotificationEnum.ExtraJobDone:
        return this.resolveExtraJobRedirectTarget$(details, log.entityId);

      case InAppNotificationEnum.PaymentReceived:
      case InAppNotificationEnum.PaymentProcess:
      case InAppNotificationEnum.PaymentApproved:
      case InAppNotificationEnum.PaymentRejected:
        return of(this.resolvePaymentRedirectTarget(details));

      case InAppNotificationEnum.FileReady:
        return of(null);

      default:
        return of(log.entityId ? this.buildJobRedirectTarget(log.entityId) : null);
    }
  }

  private resolveExtraJobRedirectTarget$(details: NotificationDetails, entityId?: string | null): Observable<NotificationRedirectTarget> {
    if (details.jobId) {
      return of(this.buildExtraJobRedirectTarget(details.jobId, details.extraJobId ?? entityId ?? null));
    }

    if (!entityId) {
      return of(this.buildJobsFallbackTarget());
    }

    return this.extraJobApi.find$(entityId).pipe(
      map((extraJob) => {
        if (!extraJob?.jobId) {
          return this.buildJobsFallbackTarget();
        }

        return this.buildExtraJobRedirectTarget(extraJob.jobId, extraJob.id ?? entityId);
      }),
      catchError(() => of(this.buildJobsFallbackTarget())),
    );
  }

  private resolvePaymentRedirectTarget(details: NotificationDetails): NotificationRedirectTarget {
    if (details.jobId) {
      if (details.extraJobId) {
        return this.buildExtraJobRedirectTarget(details.jobId, details.extraJobId);
      }

      return this.buildJobRedirectTarget(details.jobId);
    }

    return this.buildPaymentsFallbackTarget();
  }

  private buildJobRedirectTarget(jobId: string): NotificationRedirectTarget {
    return {
      url: this.appRoutes.jobDetail(jobId),
    };
  }

  private buildExtraJobRedirectTarget(jobId: string, extraJobId?: string | null): NotificationRedirectTarget {
    return {
      url: this.appRoutes.jobDetail(jobId),
      queryParams: buildExtraJobsTabQueryParams(extraJobId),
    };
  }

  private buildJobsFallbackTarget(): NotificationRedirectTarget {
    return {
      url: this.appRoutes.jobs(),
    };
  }

  private buildPaymentsFallbackTarget(): NotificationRedirectTarget {
    return {
      url: this.appRoutes.payments(),
    };
  }

  private parseDetails(details?: string | null): NotificationDetails {
    if (!details) {
      return {};
    }

    try {
      const parsed = JSON.parse(details);

      if (!parsed || typeof parsed !== 'object') {
        return {};
      }

      return {
        jobId: typeof parsed.jobId === 'string' ? parsed.jobId : null,
        extraJobId: typeof parsed.extraJobId === 'string' ? parsed.extraJobId : null,
      };
    } catch {
      return {};
    }
  }
}
